// utils/websocket.js
class WebSocketService {
    constructor() {
        this.socket = null;
        this.reconnectTimer = null;
        this.reconnectCount = 0;
        this.maxReconnect = 5;
        this.listeners = {};
        this.heartbeatInterval = null;
        this.isManualClose = false;
    }

    connect(url, protocols = []) {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            console.log('WebSocket already connected');
            return;
        }

        this.isManualClose = false;
        this.url = url;

        try {
            this.socket = new WebSocket(url, protocols);
            this.setupEventListeners();
        } catch (error) {
            console.error('WebSocket connection error:', error);
            this.trigger('error', error);
        }
    }

    setupEventListeners() {
        this.socket.onopen = (event) => {
            console.log('WebSocket connected');
            this.reconnectCount = 0;
            this.startHeartbeat();
            this.trigger('open', event);
        };

        this.socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                this.trigger('message', data);
            } catch (e) {
                this.trigger('message', event.data);
            }
        };

        this.socket.onerror = (error) => {
            console.error('WebSocket error:', error);
            this.trigger('error', error);
        };

        this.socket.onclose = (event) => {
            console.log('WebSocket closed:', event.code, event.reason);
            this.stopHeartbeat();
            this.trigger('close', event);

            // 自动重连
            if (!this.isManualClose && this.reconnectCount < this.maxReconnect) {
                this.reconnect();
            }
        };
    }

    send(data) {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            const message = typeof data === 'object' ? JSON.stringify(data) : data;
            this.socket.send(message);
            return true;
        } else {
            console.warn('WebSocket is not connected');
            return false;
        }
    }

    reconnect() {
        this.reconnectCount++;
        console.log(`Reconnecting... (${this.reconnectCount}/${this.maxReconnect})`);

        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = setTimeout(() => {
            this.connect(this.url);
        }, Math.min(1000 * Math.pow(2, this.reconnectCount), 10000));
    }

    startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatInterval = setInterval(() => {
            if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                this.send({
                    type: 'heartbeat',
                    timestamp: Date.now(),
                });
            }
        }, 30000);
    }

    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    close(code = 1000, reason = 'Normal closure') {
        this.isManualClose = true;
        if (this.socket) {
            this.socket.close(code, reason);
        }
    }

    on(event, callback) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        this.listeners[event].push(callback);
    }

    off(event, callback) {
        if (this.listeners[event]) {
            this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback);
        }
    }

    trigger(event, data) {
        if (this.listeners[event]) {
            this.listeners[event].forEach((callback) => callback(data));
        }
    }

    get readyState() {
        return this.socket ? this.socket.readyState : WebSocket.CLOSED;
    }

    get isConnected() {
        return this.socket && this.socket.readyState === WebSocket.OPEN;
    }
}

export default new WebSocketService();
